import { IconButton, DragButton } from '@ardoq/button';
import { numericSpace } from '@ardoq/design-tokens';
import { IconName } from '@ardoq/icons';
import { FlexBox } from '@ardoq/layout';
import { ICellRendererParams } from 'ag-grid-community';
import { ComponentType, useEffect, useRef } from 'react';
import { Row } from './types';
import { AGGridTableButtonWrapper } from '../Cells';
import { DEFAULT_GROUP_CELL_LABELS, GroupCellLabels } from '../labels';

type GroupCellRendererParams = {
  /** Component to render the cell content. Receives the row data spread as props. */
  InnerCellRenderer: ComponentType<Record<string, unknown>>;
  /** Show drag handle for reordering. Only applies to tree data grouping. */
  showDragHandle: boolean;
  /** Column ID to get data from. Required for tree data grouping, not used for row grouping. */
  columnId: string;
  /** Optional translated labels for the group cell affordances. */
  labels?: Partial<GroupCellLabels>;
};

type GroupCellRendererProps = ICellRendererParams<Row<any>> &
  GroupCellRendererParams;

const useInnerCellRenderer = (props: GroupCellRendererProps) => {
  // For tree data: get content from row data via columnId
  // For row grouping: use the group key
  const cellData = props.columnId
    ? props.data?.data?.[props.columnId]
    : props.node.key;

  const { InnerCellRenderer } = props;

  return InnerCellRenderer &&
    typeof cellData === 'object' &&
    cellData !== null ? (
    <InnerCellRenderer {...cellData} />
  ) : (
    <>{cellData}</>
  );
};

const useAgGridDragHandle = (props: GroupCellRendererProps) => {
  const dragHandleRef = useRef<HTMLButtonElement>(null);

  const { showDragHandle, registerRowDragger } = props;

  // Register the drag handle with AG Grid so it knows which element initiates dragging
  // Note: AG Grid handles cleanup when the row is destroyed, so no unregister is needed
  useEffect(() => {
    if (showDragHandle && dragHandleRef.current && registerRowDragger) {
      registerRowDragger(dragHandleRef.current);
    }
  }, [showDragHandle, registerRowDragger]);

  return dragHandleRef;
};

export const GroupCellRenderer = (props: GroupCellRendererProps) => {
  // Read expandedIds from context (set via api.setGridOption('context', ...))
  // This ensures we get fresh data on each refresh, avoiding AG Grid's cellRendererParams caching
  const expandedIds: string[] = props.context?.expandedIds ?? [];

  const nodeId = props.node.id!;
  const isExpanded = expandedIds.includes(nodeId);

  const handleChevronClick = () => {
    // Read the toggle from context (a stable wrapper kept fresh via the grouping
    // ref). cellRendererParams is cached per cell and can go permanently stale.
    props.context?.onRowExpansionToggle?.(nodeId, !isExpanded);
  };

  const dragHandleRef = useAgGridDragHandle(props);
  const children = useInnerCellRenderer(props);

  const canHaveChildNodes = props.node.data?.allowDrop ?? false;
  const hasChildren = (props.node.allChildrenCount ?? 0) > 0;

  return (
    <GroupCellWrapper
      showDragHandle={!!props.showDragHandle}
      canHaveChildNodes={canHaveChildNodes}
      hasChildren={hasChildren}
      isExpanded={isExpanded}
      node={props.node}
      handleChevronClick={handleChevronClick}
      dragHandleRef={dragHandleRef}
      labels={props.labels}
    >
      {children}
    </GroupCellWrapper>
  );
};

type GroupCellWrapperProps = {
  showDragHandle: boolean;
  hasChildren: boolean;
  canHaveChildNodes: boolean;
  isExpanded: boolean;
  node: ICellRendererParams['node'];
  handleChevronClick: VoidFunction;
  dragHandleRef?: React.RefObject<HTMLButtonElement | null>;
  labels?: Partial<GroupCellLabels>;
};

export const GroupCellWrapper = (
  props: React.PropsWithChildren<GroupCellWrapperProps>
) => {
  const labels = { ...DEFAULT_GROUP_CELL_LABELS, ...props.labels };
  return (
    <FlexBox
      align="center"
      height="full"
      style={{ marginLeft: props.node.level * INDENTATION_PER_LEVEL }}
      aria-expanded={props.canHaveChildNodes ? props.isExpanded : undefined}
    >
      {props.showDragHandle && (
        <DragButton ref={props.dragHandleRef} aria-label={labels.dragHandle} />
      )}
      {props.hasChildren ? (
        <AGGridTableButtonWrapper>
          <IconButton
            label={props.isExpanded ? labels.collapseGroup : labels.expandGroup}
            onClick={props.handleChevronClick}
            iconName={
              props.isExpanded ? IconName.CHEVRON_DOWN : IconName.CHEVRON_RIGHT
            }
          />
        </AGGridTableButtonWrapper>
      ) : (
        <span style={{ width: `${ICON_BUTTON_WIDTH}px`, flexShrink: 0 }} />
      )}
      {props.children}
    </FlexBox>
  );
};

const INDENTATION_PER_LEVEL = numericSpace.medium;
const ICON_BUTTON_WIDTH = 34; // Width of IconButton in px
